import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can, type SessionDetail, type TeamSummary, type TemplateSummary } from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../../lib/api';
import { today } from '../../lib/dates';
import { Alert, Button, Dialog, Field } from '../ui';
import { TimeField } from '../TimeField';

const LAST_KEY = 'hoopify.lastSession';

/** Remembers the last time and length used, since most teams train at the same time each week. */
function lastUsed(): { startTime: string; durationMinutes: string } {
  try {
    const v = JSON.parse(localStorage.getItem(LAST_KEY) ?? '{}');
    return { startTime: v.startTime ?? '17:00', durationMinutes: String(v.durationMinutes ?? 90) };
  } catch {
    return { startTime: '17:00', durationMinutes: '90' };
  }
}

export function NewSessionDialog({ open, onClose, teamId: fixedTeam, date: initialDate }: { open: boolean; onClose: () => void; teamId?: string; date?: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => api.get<TeamSummary[]>('/teams'), enabled: open });
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api.get<TemplateSummary[]>('/templates'), enabled: open });
  const coachTeams = (teams.data ?? []).filter((t) => can(t.role, 'sessions:write'));

  const [teamId, setTeamId] = useState(fixedTeam ?? '');
  const [title, setTitle] = useState('Training');
  const [date, setDate] = useState(initialDate ?? today());
  const [startTime, setStartTime] = useState(lastUsed().startTime);
  const [duration, setDuration] = useState(lastUsed().durationMinutes);
  const [templateId, setTemplateId] = useState('');

  useEffect(() => {
    if (open) {
      setDate(initialDate ?? today());
      setTeamId(fixedTeam ?? '');
      const l = lastUsed();
      setStartTime(l.startTime);
      setDuration(l.durationMinutes);
    }
  }, [open, initialDate, fixedTeam]);
  useEffect(() => {
    if (!teamId && coachTeams.length === 1) setTeamId(coachTeams[0]!.id);
  }, [coachTeams, teamId]);

  const create = useMutation({
    mutationFn: () =>
      api.post<SessionDetail>(`/teams/${teamId}/sessions`, {
        title,
        date,
        startTime,
        durationMinutes: duration,
        ...(templateId ? { templateId } : {}),
      }),
    onSuccess: (s) => {
      try {
        localStorage.setItem(LAST_KEY, JSON.stringify({ startTime, durationMinutes: Number(duration) }));
      } catch {
        /* not remembered: fine */
      }
      void qc.invalidateQueries({ queryKey: ['schedule'] });
      void qc.invalidateQueries({ queryKey: ['team-sessions'] });
      onClose();
      navigate(`/teams/${s.teamId}/sessions/${s.id}`);
    },
  });
  const fields = fieldErrors(create.error);

  const pickTemplate = (id: string) => {
    setTemplateId(id);
    const t = templates.data?.find((x) => x.id === id);
    if (t) {
      setTitle(t.title);
      setDuration(String(t.durationMinutes));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="New session">
      {teams.data && !coachTeams.length ? (
        <p className="text-muted">You need to be a head or assistant coach on a team to plan sessions.</p>
      ) : (
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }} className="space-y-4" noValidate>
          {create.isError && !Object.keys(fields).length && <Alert>{errorMessage(create.error)}</Alert>}
          {!fixedTeam && (
            <div className="space-y-1.5">
              <label htmlFor="ns-team" className="block text-[14px] font-medium">
                Team
              </label>
              <select id="ns-team" className="field-input" value={teamId} onChange={(e) => setTeamId(e.target.value)} required>
                <option value="" disabled>
                  Choose a team
                </option>
                {coachTeams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!!templates.data?.length && (
            <div className="space-y-1.5">
              <label htmlFor="ns-template" className="block text-[14px] font-medium">
                Start from
              </label>
              <select id="ns-template" className="field-input" value={templateId} onChange={(e) => pickTemplate(e.target.value)}>
                <option value="">A blank plan</option>
                {templates.data.map((t) => (
                  <option key={t.id} value={t.id}>
                    Template: {t.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Field label="Name" value={title} onChange={(e) => setTitle(e.target.value)} error={fields.title} maxLength={120} required />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Field label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} error={fields.date} required className="field-input col-span-2 sm:col-span-1" />
            <TimeField label="Starts" value={startTime} onChange={setStartTime} error={fields.startTime} required />
            <Field label="Length (min)" type="number" min={10} max={360} step={5} value={duration} onChange={(e) => setDuration(e.target.value)} error={fields.durationMinutes} required className="field-input tnum" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending} disabled={!teamId}>
              Create and plan
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
